import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import SmsNotifications from "@/components/SmsNotifications";
import { X, GearSix, Microphone, House, WaveTriangle, SlidersHorizontal, VideoCamera, Sparkle, Lightning, CheckSquare, ChatCircleText, Faders, DeviceMobile } from "@phosphor-icons/react";

function Toggle({ checked, onChange, disabled, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      data-testid={testid}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative w-12 h-7 rounded-full border-2 border-black transition-colors shrink-0 ${checked ? "bg-primary" : "bg-white"} ${disabled ? "opacity-40 cursor-not-allowed" : "press"}`}
    >
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full border-2 border-black bg-white transition-transform ${checked ? "translate-x-5" : "translate-x-0"}`} />
    </button>
  );
}

function Row({ Icon, title, desc, children }) {
  return (
    <div className="flex items-start justify-between gap-3 p-4 rounded-xl border-2 border-black bg-white">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg border-2 border-black flex items-center justify-center bg-[#F3F4F6] shrink-0"><Icon size={18} weight="bold" /></div>
        <div>
          <p className="font-bold text-sm">{title}</p>
          <p className="text-xs text-black/50 mt-0.5 leading-snug">{desc}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

export default function SettingsPanel({ open, onClose, onCustomizeToolbar, onCustomizeDashboard }) {
  const { user, prefs, savePrefs, refreshUser } = useAuth();
  const isOwner = user?.role === "owner";
  const [local, setLocal] = useState(prefs);
  const [allowEmpTasks, setAllowEmpTasks] = useState(false);
  const [allowCommNotes, setAllowCommNotes] = useState(false);
  const [globalLoaded, setGlobalLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [smsTesting, setSmsTesting] = useState(false);
  const [smsTestResult, setSmsTestResult] = useState(null);
  const [phoneInput, setPhoneInput] = useState("");
  const [phoneSaving, setPhoneSaving] = useState(false);

  useEffect(() => { if (open) setPhoneInput(user?.phone || ""); }, [open, user]);

  const saveMyPhone = async () => {
    setPhoneSaving(true);
    try {
      await api.put("/me/phone", { phone: phoneInput.trim() });
      await refreshUser();
      setSmsTestResult(null);
      toast.success(phoneInput.trim() ? "Mobile number saved" : "Mobile number removed");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setPhoneSaving(false); }
  };

  const sendTestSms = async () => {
    setSmsTesting(true); setSmsTestResult(null);
    try {
      const { data } = await api.post("/sms/test");
      setSmsTestResult(data);
      if (data.mode === "demo") toast.success("Demo text generated — see the preview below");
      else if (data.status === "sent") toast.success("Test text sent!");
      else toast.error("Test text could not be sent");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSmsTesting(false); }
  };

  useEffect(() => { if (open) setLocal(prefs); }, [open, prefs]);
  useEffect(() => {
    if (open && isOwner) api.get("/settings/global").then((r) => { setAllowEmpTasks(!!r.data.allow_employee_tasks); setAllowCommNotes(!!r.data.allow_comm_notes); setGlobalLoaded(true); }).catch(() => {});
  }, [open, isOwner]);
  if (!open || !local) return null;

  const set = (patch) => setLocal((p) => ({ ...p, ...patch }));

  const save = async () => {
    setBusy(true);
    try {
      await savePrefs({
        voice_feature_enabled: local.voice_feature_enabled,
        voice_capture_on_startup: local.voice_capture_on_startup,
        default_opening_screen: local.voice_capture_on_startup ? "voice" : "dashboard",
        show_floating_microphone: local.show_floating_microphone,
        show_voice_navigation_tab: local.show_voice_navigation_tab,
        ai_meeting_summary_enabled: local.ai_meeting_summary_enabled,
        auto_generate_meeting_summary: local.ai_meeting_summary_enabled ? local.auto_generate_meeting_summary : false,
        sms_alerts_enabled: local.sms_alerts_enabled !== false,
      });
      if (isOwner && globalLoaded) await api.put("/settings/global", { allow_employee_tasks: allowEmpTasks, allow_comm_notes: allowCommNotes });
      toast.success("Settings saved");
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const voiceOn = local.voice_feature_enabled;

  return (
    <div className="fixed inset-0 z-[70] flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div data-testid="settings-panel" onClick={(e) => e.stopPropagation()} className="relative bg-background border-l-2 border-black w-full max-w-md h-full overflow-y-auto">
        <div className="sticky top-0 bg-[#F3E8FF] border-b-2 border-black px-5 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-secondary border-2 border-black flex items-center justify-center"><GearSix size={18} weight="fill" color="#fff" /></div>
            <h2 className="font-display text-xl font-bold">Settings</h2>
          </div>
          <button data-testid="settings-close-btn" onClick={onClose} className="press w-9 h-9 rounded-full border-2 border-black flex items-center justify-center bg-white"><X size={16} weight="bold" /></button>
        </div>

        <div className="p-5 space-y-5">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-black/40 mb-2">Voice Capture</p>
            <div className="space-y-3">
              <Row Icon={Microphone} title="Enable Voice Task Capture" desc="Master switch for all voice features across the app.">
                <Toggle testid="pref-voice-feature-enabled" checked={voiceOn} onChange={(v) => set({ voice_feature_enabled: v })} />
              </Row>
              <Row Icon={House} title="Open Voice Capture on startup" desc="Launch the Voice Capture screen automatically when you sign in.">
                <Toggle testid="pref-voice-on-startup" checked={voiceOn && local.voice_capture_on_startup} disabled={!voiceOn} onChange={(v) => set({ voice_capture_on_startup: v })} />
              </Row>
              <Row Icon={WaveTriangle} title="Quick microphone button" desc="Show the mic shortcut in the top header.">
                <Toggle testid="pref-show-floating-mic" checked={voiceOn && local.show_floating_microphone} disabled={!voiceOn} onChange={(v) => set({ show_floating_microphone: v })} />
              </Row>
              <Row Icon={SlidersHorizontal} title="Voice tab in navigation" desc="Show the Voice tab in the mobile bottom navigation.">
                <Toggle testid="pref-show-voice-nav" checked={voiceOn && local.show_voice_navigation_tab} disabled={!voiceOn} onChange={(v) => set({ show_voice_navigation_tab: v })} />
              </Row>
            </div>
          </div>

          <SmsNotifications />

          <div>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-black/40 mb-2">Meetings › AI Features</p>
            <div className="space-y-3">
              <Row Icon={Sparkle} title="Enable AI Meeting Summary" desc="Let AI organize notes, identify decisions & action items, and draft a recap. You always review before saving.">
                <Toggle testid="pref-ai-meeting-enabled" checked={!!local.ai_meeting_summary_enabled} onChange={(v) => set({ ai_meeting_summary_enabled: v })} />
              </Row>
              <Row Icon={Lightning} title="Auto-generate when meeting ends" desc="Start summary generation automatically after you end a meeting. Nothing is shared without your approval.">
                <Toggle testid="pref-ai-auto-generate" checked={!!local.ai_meeting_summary_enabled && !!local.auto_generate_meeting_summary} disabled={!local.ai_meeting_summary_enabled} onChange={(v) => set({ auto_generate_meeting_summary: v })} />
              </Row>
              {!local.ai_meeting_summary_enabled && (
                <p data-testid="ai-disabled-message" className="text-xs text-black/60 px-1">AI Meeting Summary is turned off. You can still create and edit meeting notes manually.</p>
              )}
            </div>
          </div>

          {isOwner && (
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-black/40 mb-2">Team & Communication</p>
            <div className="space-y-3">
              <Row Icon={CheckSquare} title="Allow employees to create personal tasks" desc="When on, non-managers can create tasks assigned only to themselves (kept private). Managers can still assign to anyone.">
                <Toggle testid="pref-allow-employee-tasks" checked={allowEmpTasks} onChange={setAllowEmpTasks} />
              </Row>
              <Row Icon={ChatCircleText} title="Enable communication notes" desc="Lets employees message management, and lets management reply or broadcast announcements to all employees.">
                <Toggle testid="pref-allow-comm-notes" checked={allowCommNotes} onChange={setAllowCommNotes} />
              </Row>
            </div>
          </div>
          )}

          <div>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-black/40 mb-2">Layout</p>
            <div className="space-y-3">
              <button data-testid="settings-customize-toolbar" onClick={() => onCustomizeToolbar && onCustomizeToolbar()} className="press w-full flex items-center gap-3 p-4 rounded-xl border-2 border-black bg-white text-left">
                <div className="w-9 h-9 rounded-lg border-2 border-black flex items-center justify-center bg-[#F3F4F6] shrink-0"><Faders size={18} weight="bold" /></div>
                <div><p className="font-bold text-sm">Customize toolbar</p><p className="text-xs text-black/50 mt-0.5 leading-snug">Show, hide and reorder your top toolbar icons.</p></div>
              </button>
              <button data-testid="settings-customize-dashboard" onClick={() => onCustomizeDashboard && onCustomizeDashboard()} className="press w-full flex items-center gap-3 p-4 rounded-xl border-2 border-black bg-white text-left">
                <div className="w-9 h-9 rounded-lg border-2 border-black flex items-center justify-center bg-[#F3F4F6] shrink-0"><House size={18} weight="bold" /></div>
                <div><p className="font-bold text-sm">Customize dashboard</p><p className="text-xs text-black/50 mt-0.5 leading-snug">Show, hide and reorder your dashboard widgets.</p></div>
              </button>
            </div>
          </div>

          <button data-testid="settings-save-btn" onClick={save} disabled={busy} className="press w-full h-12 rounded-full bg-primary text-white font-bold border-2 border-black hard-shadow-sm disabled:opacity-60">
            {busy ? "Saving…" : "Save changes"}
          </button>

          <div className="pt-4 mt-2 border-t border-border text-center" data-testid="settings-legal-links">
            <a href="/privacy.html" target="_blank" rel="noreferrer" className="text-xs font-bold text-black/50 underline">Privacy Policy</a>
            <span className="text-black/30 mx-2">·</span>
            <a href="/terms.html" target="_blank" rel="noreferrer" className="text-xs font-bold text-black/50 underline">Terms &amp; Conditions</a>
            <p className="text-[11px] text-black/40 mt-2">Short Stack Hawaii LLC</p>
          </div>
        </div>
      </div>
    </div>
  );
}
